import React from 'react';
import * as consts from 'config/consts';
import { getOrders } from 'config/api';
import { formatDate } from 'utils/formatter';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { NewTradeItem } from './NewTradeItem';
import { OrderItemProps } from '../DailyReport';
import { sortByNumber } from './TransactionsHistory';
import { isIosAppNotPremium } from 'components/features/utils/userStatus';
import Loading from 'components/commons/loading/Loading';

type TradeListProps = {
  targetDate: Date | null;
  onTradeItemClick: (symbol: string) => void;
};

export const TradeList = ({
  onTradeItemClick,
  targetDate,
}: TradeListProps): React.ReactElement => {
  const { data: ordersData } = useQuery('dailyOrders', fetchOrders, {
    refetchOnWindowFocus: false,
  });

  if (ordersData === null || ordersData === undefined) return <LoadingData />;

  const orders = ordersData as OrderItemProps[];

  orders.sort((first, second) =>
    sortByNumber(first.profitOrLoss, second.profitOrLoss)
  );

  return (
    <>
      <div className="flex items-center text-lg font-bold ml-4 mt-16 mb-1">
        <i className="icon-trade mr-2"></i>
        {targetDate && formatDate(targetDate, 'M月d日') + 'の取引'}
      </div>
      <div className="bg-white">
        <div className="border-b">
          {orders.length ? (
            orders.map((order, i) => {
              return (
                <NewTradeItem
                  key={i}
                  {...{
                    borderTop: i > 0 && <div className="border-b" />,
                    order,
                    onTradeItemClick,
                    targetDate,
                  }}
                />
              );
            })
          ) : (
            <div className="flex items-center justify-center mx-3 py-4 text-sm text-bluegray-500">
              {targetDate &&
                formatDate(targetDate, 'M月d日') + 'の取引はありません'}
            </div>
          )}
        </div>
        <div className="m-3 pb-4">
          <div className="flex">
            <Link
              className="text-xs text-link flex items-center ml-auto"
              to="/tradehistory">
              <div className="icon-right"></div>
              取引履歴へ
            </Link>
          </div>
          {!isIosAppNotPremium() && (
            <div className="text-xs text-bluegray-500 flex items-center justify-center mt-4">
              ※取引履歴はみんかぶ連携口座が対象となります。Moneytreeで連携している口座の取引履歴は表示されません。
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const LoadingData = () => {
  return (
    <>
      <div className="flex items-center text-lg font-bold ml-4 mt-16 mb-1">
        <i className="icon-trade mr-2"></i>--月--日の取引
      </div>
      <div className="bg-white">
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '60px',
            paddingBottom: '60px',
          }}
        />
        {!isIosAppNotPremium() && (
          <div className="m-3 pb-4">
            <div className="text-xs text-bluegray-500 flex items-center justify-center mt-4">
              ※取引履歴はみんかぶ連携口座が対象となります。Moneytreeで連携している口座の取引履歴は表示されません。
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const fetchOrders = async () => {
  const res_ = await getOrders(consts.reportType.日次);
  const res = res_.Items.map(res => {
    return {
      brokerID: res.BrokerID,
      brokerName: res.BrokerName,
      tradeTime: res.TradeTime,
      symbol: res.Symbol,
      symbolName: res.SymbolName,
      accountType: res.AccountType,
      productType: res.ProductType,
      cashMargin: res.CashMargin,
      tradeType: res.TradeType,
      side: res.Side,
      qty: res.Qty,
      price: res.Price,
      openPrice: res.OpenPrice,
      openPriceUSD: res.OpenPriceUSD,
      profitOrLoss: res.ProfitOrLoss,
      profitOrLossUSD: res.ProfitOrLossUSD,
      currencyUnit: res.CurrencyUnit,
    };
  });
  return res;
};
